/**
 * IngredientSearchReplace Component
 * Floating search interface for replacing ingredients
 */

import React, { useState, useEffect, useCallback } from "react";
import { OutgoingSearchResult } from "../types";
import { usePassioApi } from "../hooks/usePassioApi";
import { CompactFoodSummary } from "./FoodSummary";
import {
    setSearchModePreference,
    SearchMode,
} from "../utils/localStorage";
import { getEffectiveQuantity } from "utils/nutritionCalculations";

interface IngredientSearchReplaceProps {
    isOpen: boolean;
    currentItem: OutgoingSearchResult | null;
    onSelect: (newResult: OutgoingSearchResult) => void;
    onClose: () => void;
}

export const IngredientSearchReplace: React.FC<
    IngredientSearchReplaceProps
> = ({ isOpen, currentItem, onSelect, onClose }) => {
    const [searchTerm, setSearchTerm] = useState(
        currentItem?.scoredName || currentItem?.displayName || ""
    );
    const [searchResults, setSearchResults] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchMode, setSearchMode] = useState<SearchMode>(
        "smart"
    );

    // Use shared API client with proper initialization
    const { client } = usePassioApi();

    // Update search term when modal opens, but don't auto-search
    useEffect(() => {
        if (isOpen && currentItem) {
            let searchText = "";

            if (currentItem.scoredName) {
                // If there is a scored name, use that and only that
                searchText = currentItem.scoredName;
            } else if (currentItem.displayName) {
                // If there is no scored name, use the display name
                const displayName = currentItem.displayName;

                if (currentItem.brandName) {
                    // Check if brand name is already in display name (case insensitive)
                    const brandNameLower = currentItem.brandName.toLowerCase();
                    const displayNameLower = displayName.toLowerCase();

                    if (displayNameLower.includes(brandNameLower)) {
                        // Brand name is already in display name, just use display name
                        searchText = displayName;
                    } else {
                        // Brand name is not in display name, add it
                        searchText = `${currentItem.brandName} ${displayName}`;
                    }
                } else {
                    // No brand name, just use display name
                    searchText = displayName;
                }
            }

            setSearchTerm(searchText);
        } else if (isOpen && !currentItem) {
            // Clear search term for new ingredient
            setSearchTerm("");
        }
    }, [isOpen, currentItem]);

    const performSearch = useCallback(
        async (term: string) => {
            if (!client) {
                setError("API client not initialized");
                return;
            }

            setLoading(true);
            setError(null);

            try {
                // Use the appropriate search method based on mode
                const searchFunction =
                    searchMode === "smart"
                        ? client.searchFoodsSemantic
                        : client.searchFoodsBasic;
                const result = await searchFunction(term, {});
                setSearchResults(result);
                return result;
            } catch (err) {
                const errorMessage =
                    err instanceof Error
                        ? err.message
                        : "Unknown error occurred";
                setError(errorMessage);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [client, searchMode]
    );

    const handleSearch = useCallback(() => {
        if (searchTerm.trim() && client) {
            performSearch(searchTerm.trim());
        }
    }, [searchTerm, client, performSearch]);

    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
                handleSearch();
            }
            if (e.key === "Escape") {
                onClose();
            }
        },
        [handleSearch, onClose]
    );

    const handleSelect = useCallback(
        (result: OutgoingSearchResult) => {
            onSelect(result);
        },
        [onSelect]
    );

    const handleSearchModeToggle = useCallback(() => {
        const newMode: SearchMode =
            searchMode === "selective" ? "smart" : "selective";
        setSearchMode(newMode);
        setSearchModePreference(newMode);
        // Clear current results when switching modes
        setSearchResults(null);
    }, [searchMode]);

    if (!isOpen) return null;

    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
    };

    const modalStyle: React.CSSProperties = {
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        padding: "24px",
        width: "500px",
        maxWidth: "90vw",
        maxHeight: "80vh",
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        display: "flex",
        flexDirection: "column",
    };

    const headerStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
    };

    const titleStyle: React.CSSProperties = {
        fontSize: "18px",
        fontWeight: "600",
        color: "#1a202c",
    };

    const closeButtonStyle: React.CSSProperties = {
        background: "none",
        border: "none",
        fontSize: "24px",
        cursor: "pointer",
        color: "#718096",
        padding: "0",
        width: "32px",
        height: "32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    };

    const searchContainerStyle: React.CSSProperties = {
        display: "flex",
        gap: "8px",
        marginBottom: "16px",
    };

    const searchInputStyle: React.CSSProperties = {
        flex: 1,
        padding: "10px 12px",
        border: "1px solid #cbd5e0",
        borderRadius: "6px",
        fontSize: "14px",
        outline: "none",
    };

    const searchButtonStyle: React.CSSProperties = {
        padding: "10px 16px",
        backgroundColor: "#3182ce",
        color: "#ffffff",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "14px",
        fontWeight: "500",
        transition: "background-color 0.2s",
    };

    const searchModeContainerStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        marginBottom: "8px",
        gap: "8px",
        fontSize: "12px",
    };

    const searchModeLabelStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#6b7280",
        fontWeight: "400",
    };

    const searchModeToggleStyle: React.CSSProperties = {
        position: "relative",
        width: "36px",
        height: "20px",
        backgroundColor: searchMode === "smart" ? "#10b981" : "#e5e7eb",
        borderRadius: "10px",
        cursor: "pointer",
        transition: "background-color 0.3s",
        border: "none",
        padding: "0",
    };

    const searchModeToggleHandleStyle: React.CSSProperties = {
        position: "absolute",
        top: "2px",
        left: searchMode === "smart" ? "18px" : "2px",
        width: "16px",
        height: "16px",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
        transition: "left 0.3s",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
    };

    const resultsContainerStyle: React.CSSProperties = {
        flex: 1,
        overflowY: "auto",
        maxHeight: "400px",
    };

    const loadingStyle: React.CSSProperties = {
        textAlign: "center",
        padding: "40px",
        color: "#718096",
    };

    const noResultsStyle: React.CSSProperties = {
        textAlign: "center",
        padding: "40px",
        color: "#718096",
    };

    const errorStyle: React.CSSProperties = {
        textAlign: "center",
        padding: "40px",
        color: "#e53e3e",
    };

    const currentItemStyle: React.CSSProperties = {
        padding: "12px",
        backgroundColor: "#f7fafc",
        borderRadius: "6px",
        marginBottom: "16px",
        border: "2px solid #3182ce",
    };

    const currentItemLabelStyle: React.CSSProperties = {
        fontSize: "12px",
        fontWeight: "600",
        color: "#3182ce",
        textTransform: "uppercase",
        marginBottom: "8px",
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={headerStyle}>
                    <h3 style={titleStyle}>
                        {currentItem
                            ? "Replace Ingredient"
                            : "Add New Ingredient"}
                    </h3>
                    <button
                        onClick={onClose}
                        style={closeButtonStyle}
                        onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#2d3748")
                        }
                        onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#718096")
                        }
                    >
                        ×
                    </button>
                </div>

                {/* Current Item - only show when replacing existing ingredient */}
                {currentItem && (
                    <div style={currentItemStyle}>
                        <div style={currentItemLabelStyle}>
                            Current Ingredient
                        </div>
                        <CompactFoodSummary
                            food={currentItem}
                            style={{ marginBottom: "0px" }}
                            quantity={getEffectiveQuantity(currentItem.nutritionPreview?.portion)}
                        />
                    </div>
                )}

                {/* Search Mode Toggle */}
                <div style={searchModeContainerStyle}>
                    <span style={searchModeLabelStyle}>Search mode:</span>
                    <button
                        onClick={handleSearchModeToggle}
                        style={searchModeToggleStyle}
                        aria-label="Toggle search mode"
                        title={`Currently using ${searchMode} search. Click to switch.`}
                    >
                        <div style={searchModeToggleHandleStyle} />
                    </button>
                    <span
                        style={{
                            ...searchModeLabelStyle,
                            color:
                                searchMode === "smart" ? "#10b981" : "#6b7280",
                            fontWeight: searchMode === "smart" ? "500" : "400",
                        }}
                    >
                        {searchMode === "smart"
                            ? "Smart Search"
                            : "Targetted Search"}
                    </span>
                </div>

                {/* Search Input */}
                <div style={searchContainerStyle}>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={
                            currentItem
                                ? "Edit name and press Enter or click Search"
                                : "Enter ingredient name and press Enter or click Search"
                        }
                        style={searchInputStyle}
                        autoFocus
                    />
                    <button
                        onClick={handleSearch}
                        disabled={loading || !client}
                        style={searchButtonStyle}
                        onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#2c5aa0")
                        }
                        onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "#3182ce")
                        }
                    >
                        {loading ? "Searching..." : "Search"}
                    </button>
                </div>

                {/* Search Results */}
                <div style={resultsContainerStyle}>
                    {loading && (
                        <div style={loadingStyle}>
                            Searching for ingredients
                            {searchMode === "smart"
                                ? " (using AI-powered search)"
                                : ""}
                            ...
                        </div>
                    )}

                    {error && <div style={errorStyle}>Error: {error}</div>}

                    {!loading && !error && !searchResults && (
                        <div style={noResultsStyle}>
                            {currentItem
                                ? "Edit the ingredient name above and click Search or press Enter to find replacement options."
                                : "Enter an ingredient name above and click Search or press Enter to find ingredients to add."}
                        </div>
                    )}

                    {!loading &&
                        !error &&
                        searchResults &&
                        searchResults.results.length === 0 && (
                            <div style={noResultsStyle}>
                                No ingredients found. Try a different search
                                term
                                {searchMode === "selective"
                                    ? " or switch to Smart Search"
                                    : ""}
                                .
                            </div>
                        )}

                    {!loading &&
                        !error &&
                        searchResults &&
                        searchResults.results.length > 0 && (
                            <div>
                                {searchResults.results
                                    .filter(
                                        (result: OutgoingSearchResult) =>
                                            !currentItem ||
                                            result.resultId !==
                                                currentItem.resultId
                                    ) // Exclude current item if it exists
                                    .map((result: OutgoingSearchResult) => (
                                        <CompactFoodSummary
                                            key={result.resultId}
                                            food={result}
                                            quantity={getEffectiveQuantity(result.nutritionPreview?.portion)}
                                            onClick={() => handleSelect(result)}
                                            style={{ marginBottom: "4px" }}
                                        />
                                    ))}
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
};
